import MaterialRow from "@/components/MaterialRow/MaterialRow";
import { ViewChapter } from "@/components/ViewChapter/ViewChapter";
import { ViewUnit } from "@/components/ViewUnit/ViewUnit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui";
import { useAppSelector } from "@/config/store";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoCourseMaterial } from "@/shared/model/edo-course-material.model";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoUnit } from "@/shared/model/edo-unit.model";
import { IUser } from "@/shared/model/user.model";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ViewDialogType } from "@/shared/util/dialog-utils";
import { Dialog, DialogContent } from "@/components/ui";
import { IconButton } from "@/components/ui";
import { List } from "@/components/ui";
import { X } from "lucide-react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Translate } from "react-jhipster";

type ViewTeacherMaterialProps = {
  teacher: IUser | null;
  open: boolean;
  onClose: () => void;
};

const ViewTeacherMaterial = (props: ViewTeacherMaterialProps) => {
  const { loading } = useAppSelector<EntityState<IEdoFile>>(
    (state) => state.edoFile
  );
  const [tabValue, setTabValue] = useState<number | false>(false);
  const [courses, setCourses] = useState<IEdoCourse[]>([]);
  const [teacherMaterials, setTeacherMaterials] = useState<IEdoCourseMaterial[]>(
    []
  );
  const [viewUnitDialog, setViewUnitDialog] = useState<boolean>(false);
  const [unitToView, setUnitToView] = useState<IEdoUnit | null>(null);
  const [viewChapterDialog, setViewChapterDialog] =
    useState<ViewDialogType<IEdoChapter>>({ open: false, entity: null });

  const { teacher, open, onClose: handleClose } = props;

  const handleChangeTab = (newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      if (!teacher?.id) return;
      const response = await axios.get<{ data: IEdoCourse[] }>(
        `/teachers/${teacher.id}/courses`
      );
      setCourses(response.data.data);
    };
    if (open) {
      fetchCourses();
    }
  }, [open, teacher?.id]);

  useEffect(() => {
    const fetchMaterials = async (courseId: number) => {
      const response = await axios.get<{ data: IEdoCourseMaterial[] }>(
        `/courses/${courseId}/course-materials`,
        {
          params: {
            page: 0,
            limit: 1000,
            sortBy: "order:ASC",
            "filter.author.id": "$eq:" + teacher?.id,
          },
        }
      );
      setTeacherMaterials(response.data.data);
    };
    if (teacher?.id && tabValue !== false) {
      fetchMaterials(tabValue);
    }
  }, [tabValue, teacher?.id]);

  useEffect(() => {
    if (courses.length > 0) {
      setTabValue(courses[0].id);
    }
  }, [courses]);

  const handleCloseViewUnitDialog = () => {
    setViewUnitDialog(false);
    setUnitToView(null);
  };

  const getCourseMaterials = () => {
    if (!teacherMaterials.length) {
      return (
        <div className="p-4 text-center">
          <p className="text-body text-[var(--color-mockup-text-mid)]">
            <Translate contentKey="edocendoApp.edoCourseMaterial.home.notFound" />
          </p>
        </div>
      );
    }
    return (
      <List className="divide-y" style={{ listStyle: "none" }}>
        {teacherMaterials.map((teacherMaterial, index, list) => (
          <MaterialRow
            key={teacherMaterial.id}
            courseMaterial={teacherMaterial}
            isLastElement={list.length - 1 === index}
            onOpenViewChapter={() =>
              setViewChapterDialog({
                open: true,
                entity: teacherMaterial.chapter,
              })
            }
          />
        ))}
      </List>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent
          showClose={false}
          className="flex max-h-[90vh] w-full max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:rounded-2xl"
        >
          {loading ? (
            <div className="flex justify-center py-8">
              <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] px-6 py-4 dark:border-[var(--color-mockup-border)]">
                <h2 className="text-heading font-semibold text-[var(--color-mockup-text)]">
                  Materiale docente
                </h2>
                <IconButton
                  onClick={handleClose}
                  aria-label="Chiudi"
                  variant="ghost"
                  size="sm"
                >
                  <X className="h-4 w-4" />
                </IconButton>
              </div>
              {courses.length > 0 && (
                <Tabs
                  value={tabValue === false ? "" : String(tabValue)}
                  onValueChange={(v) => handleChangeTab(Number(v))}
                  className="flex min-h-0 flex-1 flex-col overflow-hidden"
                >
                  <TabsList className="mx-6 mt-4 flex w-auto shrink-0 gap-1 self-start overflow-x-auto border-0 bg-transparent p-0">
                    {courses.map((course) => (
                      <TabsTrigger
                        key={course.id}
                        value={String(course.id)}
                        className="whitespace-nowrap rounded-md border border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-3 py-2 data-[state=active]:bg-primary data-[state=active]:text-white dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/50 dark:data-[state=active]:bg-primary"
                      >
                        {course.subject?.name} - {course.class?.year?.year}°
                        {course.class?.section} ({course.class?.school?.code})
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {courses.map((course) => (
                    <TabsContent
                      key={course.id}
                      value={String(course.id)}
                      className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4"
                    >
                      {tabValue === course.id ? getCourseMaterials() : null}
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <ViewUnit
        unit={unitToView}
        open={viewUnitDialog}
        handleClose={handleCloseViewUnitDialog}
      />
      <ViewChapter
        open={viewChapterDialog.open}
        chapter={viewChapterDialog.entity}
        onClose={() => setViewChapterDialog({ open: false, entity: null })}
      />
    </>
  );
};

export default ViewTeacherMaterial;
